import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faFlag,
  faFilePdf,
  faCommentDots,
  faStar,
  faChevronDown,
  faChevronRight,
  faTriangleExclamation,
  faChartSimple,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { getApiUrl, SERVERS, ENDPOINTS } from "../config/api";
import { isInstructorOnly } from "../config/roles";
// Reading a rubric verdict lives in one place now. These were defined here and
// copied, differently, into AnalyzeQuestionPapers.jsx - which is how that page
// came to crash on a 0-4 verdict by calling .split() on a number.
import {
  BLOOM_NAMES,
  criteriaSummary,
  qualityBand,
  questionLabel,
} from "../config/rubric";
import Disclosure from "../components/Disclosure";
import { QuestionRubric, QuestionRubricSummary } from "../components/QuestionRubric";
import PaperComparison from "../components/PaperComparison";
import QpEvaluation from "../components/QpEvaluation";
import "./Review.css";

// The Reviewer email field means one thing and does two, and what it does
// depends on who is looking.
//
// For an instructor it is their own address, filled in from the session and
// locked. The backend narrows the listing to the courses the HOG mapped to them
// and takes the address from the session rather than from this parameter, so
// letting them retype it would be theatre.
//
// For an admin it is a lookup: name a reviewer and you get that reviewer's
// assigned courses, attributed to them. Leave it blank and you get the whole
// catalogue. It starts blank deliberately - it used to default to one
// professor's address, hardcoded, which meant an admin who signed off a paper
// without noticing the field filed their review under that person's name.

const EMPTY_FILTERS = {
  academicYear: "",
  semester: "",
  courseCode: "",
  courseName: "",
  examType: "",
  examFormat: "",
  uploaderName: "",
  instructorName: "",
  reviewStatus: "",
};

// The `source` filter is deliberately absent. GET /review/question-papers still
// accepts it, and the listing still reports which pipeline produced each paper -
// the upload/robot icon in the comparison table is drawn from that - so nothing
// on either side was removed. It was the control that had to go: a paper is
// found by what it IS, and which pipeline filed it is provenance rather than a
// property of the paper anyone searches on.

// Query params this page accepts as incoming filters, mapped to the form fields
// they populate. Automated Evaluation links here with the same metadata it just
// searched on, so arriving that way lands on the same set it previewed instead of
// making the user retype it.
//
// Query params rather than router state: they survive a reload, can be shared or
// bookmarked, and are visible - state passed through navigate() is gone the
// moment the page is refreshed, which is exactly when someone would want the
// filters back.
//
// `batch` is deliberately absent. Automated Evaluation accepts it, but
// question_papers has no batch column, so it never filters stored results on
// either side. Carrying it would put a filter in the form that changes nothing.
const FILTER_PARAMS = {
  academic_year: "academicYear",
  semester: "semester",
  course_id: "courseCode",
  course_name: "courseName",
  exam_type: "examType",
  exam_format: "examFormat",
  uploader_name: "uploaderName",
  instructor_name: "instructorName",
  review_status: "reviewStatus",
};

// A failed create/fetch of the review record used to be dropped on the floor
// silently - not even a console.error - which is worse than any other failure
// on this page precisely because it can be the ownership check added for the
// per-reviewer visibility fix (_require_own_email_if_instructor, HTTP 403).
// That case gets its own message: `body.detail` on a 403 is already
// "You may only access your own review records.", but read in isolation (no
// paper, no context) it reads like a generic permissions message rather than
// telling the reviewer why one specific record was blocked.
async function describeReviewLoadFailure(response) {
  const body = await response.json().catch(() => ({}));
  if (response.status === 403) {
    return "You don't have access to this reviewer's record.";
  }
  return body.detail || "Could not load the review record for this paper.";
}

const QualityBadge = ({ score }) => {
  if (score == null) return null;
  const value = Number(score);
  if (!Number.isFinite(value)) return null;
  return (
    <span className={`qr-quality is-${qualityBand(value)}`} title="Question quality score">
      <span className="qr-quality-label">Quality</span>
      <span className="qr-quality-value">{Number(value.toFixed(1))}</span>
      <span className="qr-quality-max">/10</span>
    </span>
  );
};

const Review = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewerEmail, setReviewerEmail] = useState("");
  // Locked once the session turns out to belong to an instructor. Starts false
  // so the field is never briefly locked to the wrong address on first paint.
  const [lockReviewerEmail, setLockReviewerEmail] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [arrivedWithFilters, setArrivedWithFilters] = useState(false);

  const [papers, setPapers] = useState(null); // null = no search run yet
  const [selectedPaperId, setSelectedPaperId] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [paperPdfUrl, setPaperPdfUrl] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [isLoadingPaper, setIsLoadingPaper] = useState(false);

  const [paperReview, setPaperReview] = useState(null);
  const [questionReviews, setQuestionReviews] = useState({});
  const [paperFeedback, setPaperFeedback] = useState("");
  // Bug #21: the debounced review-record load below used to overwrite
  // paperFeedback/questionReviews unconditionally whenever its GET resolved -
  // including after the reviewer had already started typing a comment. A
  // slow-enough GET (real network variance, not contrived) would then land
  // *after* the keystroke but the overwrite carried no signal of that, so it
  // silently replaced what they'd typed with the stale value the GET had just
  // fetched - and if they saved right after, that stale value is what got
  // written back, with no error and no indication anything was lost. Refs,
  // not state: these exist purely to gate that one effect and reading them
  // must never itself trigger a re-render.
  const paperFeedbackTouchedRef = useRef(false);
  const touchedQuestionFeedbackRef = useRef(new Set());
  const [savingQuestionId, setSavingQuestionId] = useState("");
  // The question whose decision was just written, so the confirmation can
  // appear where the click happened. The page-level notice at the top said the
  // same thing, which meant scrolling up to find out whether a button had
  // worked - and the header badge is above the buttons too.
  const [justSavedQuestionId, setJustSavedQuestionId] = useState("");
  const [isSigningOff, setIsSigningOff] = useState(false);
  // Questions the reviewer has opened. Closed is the default and the empty
  // object means "all closed": eight questions, each with seven criteria, is a
  // page metres long, and the header row already carries the percentage, the
  // marks, the Bloom's level, the topic and the flags - so the list reads as a
  // summary of the paper until someone asks for a particular question.
  const [openQuestions, setOpenQuestions] = useState({});
  // Which half of the review is showing. The paper's own evaluation and the
  // question-by-question review answer different questions and neither is a
  // step toward the other, so they are two panels rather than one long scroll.
  // Mirrors the tab strip on Upload Question Papers, deliberately: the same
  // gesture should look the same wherever it appears in the app.
  const [activeTab, setActiveTab] = useState("overview");

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // Courses to choose from, rather than a code to remember and type exactly.
  // Two lists, because they answer different questions: `courseOptions` is
  // every course that actually has a paper, and `assignedCourses` is what this
  // reviewer is allowed to see. The picker shows the first narrowed by the
  // second - so it never offers a course with no papers, and never offers one
  // the listing would refuse.
  const [courseOptions, setCourseOptions] = useState([]);
  const [assignedCourses, setAssignedCourses] = useState(null); // null = unscoped
  // Where the chosen paper renders. It sits below the match list, and the list
  // can be two hundred rows long - so picking a paper looked like it did
  // nothing: the evaluation appeared several thousand pixels further down the
  // page, off the bottom of the screen, and the only visible change was the
  // highlight on the row that had just been clicked.
  const paperDetailRef = useRef(null);

  const selectedPaper = (papers || []).find((p) => p.id === selectedPaperId) || null;

  // Who is looking. An instructor reviews as themselves and only sees their own
  // assigned courses, so the Reviewer email field becomes their own address,
  // filled in from the session and locked. Everyone else keeps it as a lookup
  // box. A failed session read leaves the field editable rather than locked to
  // a fallback address - guessing an identity is worse than not knowing one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/qpra/get-saml-data`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        if (isInstructorOnly(data?.roles) && data?.email) {
          setReviewerEmail(data.email);
          setLockReviewerEmail(true);
        }
      } catch (err) {
        console.error("Could not read the session for reviewer identity:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Every course with a paper. Loaded once - it is one small grouped query and
  // it does not depend on who is looking.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ALL_PAPER_COURSES));
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setCourseOptions(data.courses || []);
      } catch (err) {
        // A picker that could not load leaves the field usable as free text
        // rather than blocking the search.
        console.error("Could not load the course list:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Which of them this reviewer is assigned. Debounced, because an admin types
  // the address a character at a time and each prefix is a different person as
  // far as the server is concerned.
  useEffect(() => {
    if (!reviewerEmail) {
      setAssignedCourses(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ASSIGNED_COURSES)}`
          + `?professor_email=${encodeURIComponent(reviewerEmail)}`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setAssignedCourses(
          (data.courses || []).map((c) => (c.course_code || "").trim().toLowerCase())
            .filter(Boolean));
      } catch (err) {
        console.error("Could not load the reviewer's assigned courses:", err);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [reviewerEmail]);

  // What the picker offers. Narrowed to the reviewer's assignment exactly as the
  // backend narrows the listing - lowercased whole-string equality on the
  // course code - so the options and the results cannot disagree.
  //
  // A value already in the field that is not in the list is kept as an option
  // of its own. Arriving from a deep link with course_id=X, or having typed a
  // code by hand, would otherwise leave the select blank and silently drop the
  // filter the user is looking at.
  const visibleCourses = React.useMemo(() => {
    const allowed = assignedCourses && assignedCourses.length >= 0
      ? new Set(assignedCourses) : null;
    let list = courseOptions;
    if (allowed && reviewerEmail) {
      list = courseOptions.filter(
        (c) => allowed.has((c.course_id || "").trim().toLowerCase()));
    }
    const current = (filters.courseCode || "").trim();
    if (current && !list.some((c) => c.course_id === current)) {
      list = [{ course_id: current, course_name: null, papers: null }, ...list];
    }
    return list;
  }, [courseOptions, assignedCourses, reviewerEmail, filters.courseCode]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Arriving from Automated Evaluation with filters on the URL: fill the form in
  // and run the search without waiting for a click. The point of the link is that
  // the user has already chosen this metadata and seen how many papers it matched
  // - making them press "Find papers" again to see the same thing is a step with
  // no decision in it.
  //
  // Runs once per distinct query string. `filters` is deliberately not a
  // dependency: findPapers sets state that would retrigger this and loop.
  useEffect(() => {
    const incoming = {};
    Object.entries(FILTER_PARAMS).forEach(([param, field]) => {
      const value = searchParams.get(param);
      if (value && value.trim()) incoming[field] = value.trim();
    });
    if (Object.keys(incoming).length === 0) return;

    const next = { ...EMPTY_FILTERS, ...incoming };
    setFilters(next);
    setArrivedWithFilters(true);
    findPapers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Pressing "Find papers": search on whatever is in the form now.
  //
  // Deliberately not `onClick={findPapers}`. React passes the click event as the
  // first argument, and findPapers' `searchWith = filters` default only applies
  // when the argument is undefined - so the event was used as the filter object,
  // every `searchWith.<field>` read came back undefined, and the request went out
  // with no filters at all. The page then showed the whole catalogue while the
  // form sat there full of the values it had just ignored, and because a search
  // clears the selection, the paper being read closed as well.
  //
  // Arriving by deep link made that unmissable rather than causing it: the form
  // is populated on arrival, so the filters were visibly there and visibly
  // disregarded.
  const runManualSearch = () => {
    // The query string described the search this page arrived with, not the one
    // being run now. Leaving it in place makes the "carried over from Automated
    // Evaluation" notice describe results it no longer matches, and makes a
    // reload silently discard the user's edits and re-run the old search.
    //
    // Safe against the auto-run effect below: clearing the params changes its
    // dependency, it re-runs, finds nothing incoming and returns.
    if (searchParams.toString()) setSearchParams({}, { replace: true });
    setArrivedWithFilters(false);
    findPapers(filters);
  };

  // Takes the filters to search with explicitly, defaulting to what is in the
  // form. The auto-run below needs to pass them directly: setFilters() has not
  // been applied to state yet at that point, so reading `filters` there would
  // search on the previous (empty) values and return the whole catalogue.
  const findPapers = async (searchWith = filters) => {
    setIsSearching(true);
    setError("");
    setNotice("");
    setPapers(null);
    setSelectedPaperId("");
    setAnalysis(null);
    setPaperReview(null);

    try {
      const params = new URLSearchParams();
      if (reviewerEmail) params.append("reviewer_email", reviewerEmail);
      if (searchWith.academicYear) params.append("academic_year", searchWith.academicYear);
      if (searchWith.semester) params.append("semester", searchWith.semester);
      if (searchWith.courseCode) params.append("course_code", searchWith.courseCode);
      if (searchWith.courseName) params.append("course_name", searchWith.courseName);
      if (searchWith.examType) params.append("exam_type", searchWith.examType);
      if (searchWith.examFormat) params.append("exam_format", searchWith.examFormat);
      if (searchWith.uploaderName) params.append("uploader_name", searchWith.uploaderName);
      if (searchWith.instructorName) params.append("instructor_name", searchWith.instructorName);
      if (searchWith.reviewStatus) params.append("review_status", searchWith.reviewStatus);

      const response = await fetch(
        `${getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_QUESTION_PAPERS)}?${params}`
      );
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.detail || "Failed to fetch question papers");
      }

      // Newest first. The backend already orders by created_at desc, but sorting
      // here as well means the order holds whatever a caller does upstream - and
      // "the one I just uploaded" is the single most common thing to be looking
      // for, so it should be the first thing in the list.
      const found = [...result.data].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setPapers(found);
      if (found.length === 0) {
        // Distinguish "these filters match nothing" from "this reviewer has
        // nothing assigned". The second is not a filter problem, and saying
        // "no papers match these filters" about it sends the user off adjusting
        // the wrong thing.
        const scope = result.filters_applied || {};
        const named = scope.assigned_to;
        const assigned = scope.assigned_courses;
        if (named && Array.isArray(assigned) && assigned.length === 0) {
          setError(
            `${named} has no courses assigned in any HOG mapping, so there is `
            + `nothing to show. Check the address, or assign them a course in `
            + `Reviewer Assigner.`);
        } else {
          setError("No papers match these filters.");
        }
      } else if (found.length === 1) {
        // One match has nothing to choose between, so open it.
        setSelectedPaperId(found[0].id);
      }
      if (result.truncated) {
        setNotice(
          `Showing the first ${result.count} matches - narrow the filters to see the rest.`
        );
      }
    } catch (err) {
      console.error("Error fetching question papers:", err);
      setError(err.message || "Failed to fetch question papers.");
    } finally {
      setIsSearching(false);
    }
  };

  // Bring the evaluation into view when a paper is picked. Runs on the loading
  // card too, not just the finished one, so the scroll happens immediately
  // rather than after the PDF and the analysis have both arrived - waiting for
  // that is the whole reason the click felt like it had failed.
  //
  // Deliberately not on first paint from a deep link: arriving with filters in
  // the URL opens the single match on its own, and yanking the page down before
  // the reader has seen what was matched is disorienting.
  const hasSelected = useRef(false);
  useEffect(() => {
    if (!selectedPaperId) {
      hasSelected.current = false;
      return;
    }
    if (!hasSelected.current) {
      hasSelected.current = true;
      if (arrivedWithFilters) return;
    }
    // A new paper starts on its overview, and with every question folded away.
    // Carrying the previous paper's open questions over would open whichever
    // ones happen to share a position in the list, which is meaningless.
    setActiveTab("overview");
    setOpenQuestions({});
    setJustSavedQuestionId("");
    const node = paperDetailRef.current;
    if (!node) return;
    // A frame's delay: the node is being mounted by this same render pass, and
    // its position is not final until the browser has laid it out.
    const frame = requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedPaperId, isLoadingPaper, arrivedWithFilters]);

  const loadPaperPdf = useCallback(async (paperId) => {
    // Fetched as a blob rather than pointed at directly: the download endpoint
    // sends Content-Disposition: attachment, which makes a browser offer to save
    // the file instead of rendering it inline.
    const url =
      `${getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER)}/${paperId}/download`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Could not load the question paper file");
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }, []);

  // The paper itself: the original document and its analysis. Keyed on the paper
  // alone, deliberately.
  //
  // This and the review record below used to be one effect with reviewerEmail in
  // its dependency list, which meant editing that field re-downloaded the PDF and
  // re-ran the analysis. Typing one address provoked 24 of each, and every
  // keystroke reset paperReview and questionReviews on the way through - so
  // reviews already saved to the database vanished from the screen mid-typing and
  // the page looked like it had stored nothing. What a paper contains does not
  // depend on who is reviewing it, so the two no longer load together.
  useEffect(() => {
    if (!selectedPaperId) return;
    let cancelled = false;
    let createdUrl = "";

    const load = async () => {
      setIsLoadingPaper(true);
      setError("");
      setAnalysis(null);

      try {
        const [pdfUrl, analysisResult] = await Promise.all([
          loadPaperPdf(selectedPaperId).catch((err) => {
            console.error("Paper PDF load failed:", err);
            return "";
          }),
          fetch(`/analysis/analyze-paper-concurrent/${selectedPaperId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          }).then((r) => r.json()),
        ]);

        if (cancelled) {
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          return;
        }
        createdUrl = pdfUrl;
        setPaperPdfUrl(pdfUrl);

        if (analysisResult?.success) {
          setAnalysis(analysisResult.data);
        } else {
          setError(
            "This paper has no completed analysis yet, so there are no question " +
              "reviews to show. The document itself is displayed below."
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading paper:", err);
          setError(err.message || "Failed to load this paper.");
        }
      } finally {
        if (!cancelled) setIsLoadingPaper(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [selectedPaperId, loadPaperPdf]);

  // The review recorded against this paper by this reviewer. Cheap - two small
  // requests - so it can safely depend on reviewerEmail.
  //
  // Debounced anyway: an email is typed a character at a time, and each
  // intermediate prefix is a different reviewer as far as the server is
  // concerned. Without the delay every prefix creates its own paper_reviews row,
  // littering the table with records for addresses nobody has.
  useEffect(() => {
    if (!selectedPaperId) return;
    let cancelled = false;
    // A new paper (or a new reviewer identity on the same paper) starts
    // untouched - see the refs' own comment above for why this gates the
    // overwrites below rather than the fetch itself.
    paperFeedbackTouchedRef.current = false;
    touchedQuestionFeedbackRef.current = new Set();

    const timer = setTimeout(async () => {
      try {
        const created = await fetch(
          getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_REVIEW),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              question_paper_id: selectedPaperId,
              reviewer_email: reviewerEmail,
            }),
          }
        );
        if (cancelled) return;
        if (!created.ok) {
          setError(await describeReviewLoadFailure(created));
          return;
        }
        setPaperReview(await created.json());

        const existing = await fetch(
          `${getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_REVIEW)}/` +
            `${selectedPaperId}?reviewer_email=${encodeURIComponent(reviewerEmail)}`
        );
        if (cancelled) return;
        if (!existing.ok) {
          setError(await describeReviewLoadFailure(existing));
          return;
        }
        const data = await existing.json();
        // Only overwritten once an answer is in hand. Clearing these up front is
        // what made saved reviews look lost while a reload was in flight.
        //
        // Bug #21: also skipped entirely once the reviewer has typed into the
        // field this GET was sent before they finished - by the time it
        // resolves the field no longer reflects what the server had, and
        // stomping it with that stale value is exactly what silently lost
        // the reviewer's own in-progress comment (see the refs' comment).
        if (!paperFeedbackTouchedRef.current) {
          setPaperFeedback(data.paper_review?.paper_level_feedback || "");
        }
        const fetched = {};
        (data.paper_review?.question_reviews || []).forEach((qr) => {
          fetched[qr.question_id] = {
            agreement: qr.reviewer_agreement,
            feedback: qr.reviewer_feedback || "",
          };
        });
        setQuestionReviews((current) => {
          const merged = { ...fetched };
          // Same reasoning, per question: a question's own comment box the
          // reviewer has already typed into keeps what's in front of them
          // rather than being replaced by what this (possibly stale-by-now)
          // GET fetched for it.
          touchedQuestionFeedbackRef.current.forEach((questionId) => {
            if (current[questionId]) merged[questionId] = current[questionId];
          });
          return merged;
        });
      } catch (err) {
        if (!cancelled) console.error("Error loading the review record:", err);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedPaperId, reviewerEmail]);

  // The review record to attach a save to, created if this reviewer has none yet.
  //
  // Both handlers below used to bail out with "No review record for this paper
  // yet" when paperReview was null, which was reachable simply by clicking while
  // the record was still loading - the click did nothing except put a banner at
  // the top of a long page, far from wherever the user was working.
  const ensurePaperReview = async () => {
    if (paperReview) return paperReview;
    const created = await fetch(
      getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_REVIEW),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question_paper_id: selectedPaperId,
          reviewer_email: reviewerEmail,
        }),
      }
    );
    if (!created.ok) throw new Error("Could not open a review record for this paper");
    const record = await created.json();
    setPaperReview(record);
    return record;
  };


  const setQuestionDraft = (questionId, patch) => {
    setQuestionReviews((prev) => ({
      ...prev,
      [questionId]: { agreement: null, feedback: "", ...prev[questionId], ...patch },
    }));
  };

  // Record agreement or a dispute against one question's score. A dispute flags
  // the score for a later re-check; it never changes it. There is deliberately
  // no control anywhere on this page that writes marks or scores.
  const saveQuestionReview = async (question, agreement) => {
    const draft = questionReviews[question.question_id] || {};
    setSavingQuestionId(question.question_id);
    setError("");
    try {
      const review = await ensurePaperReview();
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_QUESTION_REVIEW),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paper_review_id: review.id,
            question_id: question.question_id,
            question_number: question.question_number,
            reviewer_agreement: agreement,
            reviewer_feedback: draft.feedback || null,
          }),
        }
      );
      if (!response.ok) throw new Error(`Save failed (HTTP ${response.status})`);
      setQuestionDraft(question.question_id, { agreement });
      setJustSavedQuestionId(question.question_id);
      // "saved" is a confirmation of THIS click and should fade; the decision
      // itself stays, reading "recorded" afterwards. Without the timer the last
      // question clicked would claim to have just been saved indefinitely.
      window.setTimeout(() => setJustSavedQuestionId(
        (current) => (current === question.question_id ? "" : current)), 4000);
      setNotice(
        agreement
          ? `Agreed with the score for ${questionLabel(question)}.`
          : `Flagged ${questionLabel(question)} for re-check.`
      );
    } catch (err) {
      console.error("Error saving question review:", err);
      setError(err.message || "Could not save that review.");
    } finally {
      setSavingQuestionId("");
    }
  };

  // Paper-level sign-off. Both granularities are kept because the schema
  // supports both natively: paper_reviews.paper_level_feedback / review_status
  // for the whole paper, question_reviews.reviewer_agreement / reviewer_feedback
  // per question.
  const savePaperReview = async (signOff) => {
    setIsSigningOff(true);
    setError("");
    try {
      const review = await ensurePaperReview();
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_FEEDBACK),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paper_review_id: review.id,
            paper_level_feedback: paperFeedback,
            review_status: signOff ? "completed" : "in_progress",
          }),
        }
      );
      if (!response.ok) throw new Error(`Save failed (HTTP ${response.status})`);
      const saved = await response.json();
      setPaperReview((prev) => ({ ...prev, review_status: saved.review_status }));
      setNotice(
        signOff ? "Review approved and signed off." : "Reviewer comments saved."
      );
    } catch (err) {
      console.error("Error saving paper review:", err);
      setError(err.message || "Could not save the review.");
    } finally {
      setIsSigningOff(false);
    }
  };

  const paperLabel = (paper) =>
    [paper.course_id, paper.exam_type, `${paper.academic_year} S${paper.semester}`]
      .filter(Boolean)
      .join(" · ");

  const questions = analysis?.questions_analysis || [];
  const flaggedCount = Object.values(questionReviews).filter(
    (r) => r.agreement === false
  ).length;

  return (
    <div className="review-page">
      <div className="review-header">
        <h1>
          <FontAwesomeIcon icon={faClipboardCheck} /> Review Papers
        </h1>
        <p>
          Every reviewed paper lives here, from the automated evaluation runs and
          from manual uploads alike. Filter on any combination of metadata.
        </p>
      </div>

      <div className="review-card">
        <div className="review-filters">
          <div className="review-field">
            <label htmlFor="reviewerEmail">Reviewer email</label>
            <input
              id="reviewerEmail"
              type="email"
              value={reviewerEmail}
              onChange={(e) => setReviewerEmail(e.target.value)}
              placeholder="you@wilp.bits-pilani.ac.in"
              readOnly={lockReviewerEmail}
              aria-readonly={lockReviewerEmail}
              className={lockReviewerEmail ? "review-field-locked" : undefined}
              title={lockReviewerEmail
                ? "Your own address, from your login. Reviews are attributed to you."
                : undefined}
            />
            <small>
              {lockReviewerEmail
                ? "Your own address, from your login. Reviews are attributed to you, and you see the papers for the courses you are assigned."
                : "Name a reviewer to see the courses assigned to them, and to attribute a review to them. Leave it blank to search every paper."}
            </small>
          </div>
          <div className="review-field">
            <label htmlFor="academicYear">Academic year</label>
            <input
              id="academicYear"
              name="academicYear"
              value={filters.academicYear}
              onChange={handleFilterChange}
              placeholder="2024-25"
            />
          </div>
          <div className="review-field">
            <label htmlFor="semester">Semester</label>
            <input
              id="semester"
              name="semester"
              value={filters.semester}
              onChange={handleFilterChange}
              placeholder="1 or 2"
            />
          </div>
          <div className="review-field">
            <label htmlFor="courseCode">Course code</label>
            <select
              id="courseCode"
              name="courseCode"
              value={filters.courseCode}
              onChange={handleFilterChange}
            >
              <option value="">All courses</option>
              {visibleCourses.map((course) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.course_id}
                  {course.course_name ? ` — ${course.course_name}` : ""}
                  {course.papers ? ` (${course.papers})` : ""}
                </option>
              ))}
            </select>
            <small>
              {reviewerEmail && assignedCourses
                ? assignedCourses.length === 0
                  ? "This reviewer has no courses assigned in any mapping."
                  : `${visibleCourses.length} course(s) assigned to this reviewer and holding papers.`
                : `${visibleCourses.length} course(s) with papers. Name a reviewer to narrow it.`}
            </small>
          </div>
          <div className="review-field">
            <label htmlFor="courseName">Course name</label>
            <input
              id="courseName"
              name="courseName"
              value={filters.courseName}
              onChange={handleFilterChange}
              placeholder="partial match"
            />
          </div>
          <div className="review-field">
            <label htmlFor="examType">Exam type</label>
            <input
              id="examType"
              name="examType"
              value={filters.examType}
              onChange={handleFilterChange}
              placeholder="EC3 REGULAR"
            />
          </div>
          <div className="review-field">
            <label htmlFor="examFormat">Exam format</label>
            <input
              id="examFormat"
              name="examFormat"
              value={filters.examFormat}
              onChange={handleFilterChange}
              placeholder="Open Book"
            />
          </div>
          <div className="review-field">
            <label htmlFor="instructorName">Instructor</label>
            <input
              id="instructorName"
              name="instructorName"
              value={filters.instructorName}
              onChange={handleFilterChange}
              placeholder="name or email"
            />
          </div>
          <div className="review-field">
            <label htmlFor="reviewStatus">Review status</label>
            <select
              id="reviewStatus"
              name="reviewStatus"
              value={filters.reviewStatus}
              onChange={handleFilterChange}
            >
              <option value="">Any</option>
              <option value="in_progress">Not yet signed off</option>
              <option value="completed">Signed off</option>
            </select>
          </div>
        </div>

        <div className="review-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={runManualSearch}
            disabled={isSearching}
          >
            <FontAwesomeIcon icon={isSearching ? faSpinner : faSearch} spin={isSearching} />{" "}
            {isSearching ? "Searching..." : "Find papers"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              if (searchParams.toString()) setSearchParams({}, { replace: true });
              setArrivedWithFilters(false);
              setFilters(EMPTY_FILTERS);
              setPapers(null);
              setSelectedPaperId("");
              setError("");
              setNotice("");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="review-alert review-alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} /> <span>{error}</span>
        </div>
      )}
      {arrivedWithFilters && (
        <div className="review-alert review-alert-info">
          <FontAwesomeIcon icon={faSearch} />{" "}
          <span>
            Filters carried over from Automated Evaluation and searched
            automatically. Edit any field and press <strong>Find papers</strong> to
            change the search.
          </span>
        </div>
      )}
      {notice && (
        <div className="review-alert review-alert-info">
          <FontAwesomeIcon icon={faCheckCircle} /> <span>{notice}</span>
        </div>
      )}

      {/* Several matches: a comparison, not a picker. Only rendered when there
          is an actual choice to make - a single match opens directly.

          This was a row of chips carrying a course code, an exam and two icons.
          That is enough to pick a paper you already know the name of and
          nothing at all for the question someone with twenty papers actually
          has: which of these needs attention, and is the same thing wrong with
          all of them. */}
      {papers && papers.length > 1 && (
        <PaperComparison
          papers={papers}
          selectedPaperId={selectedPaperId}
          onSelect={setSelectedPaperId}
        />
      )}

      <div ref={paperDetailRef} className="review-detail-anchor" />

      {isLoadingPaper && (
        <div className="review-card review-loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading paper and review...
        </div>
      )}

      {selectedPaper && !isLoadingPaper && (
        <>
          <div className="review-card">
            <div className="review-paper-head">
              <div>
                <h2>
                  {selectedPaper.course_id} — {selectedPaper.course_name}
                </h2>
                <p>
                  {paperLabel(selectedPaper)}
                  {selectedPaper.exam_format ? ` · ${selectedPaper.exam_format}` : ""}
                  {" · "}
                  {selectedPaper.source === "manual_upload"
                    ? "manual upload"
                    : "automated evaluation"}
                  {selectedPaper.bulk_scored ? " (bulk scored)" : ""}
                </p>
              </div>
              {selectedPaper.scored && (
                <div className="review-score-badge">
                  <FontAwesomeIcon icon={faStar} />{" "}
                  {selectedPaper.final_weighted_score != null
                    ? selectedPaper.final_weighted_score.toFixed(1)
                    : "—"}
                  <small>{selectedPaper.final_rating || ""}</small>
                </div>
              )}
            </div>

            {/* A sign-off that no longer describes the score above it. Placed
                here, directly under the score badge and above everything else,
                because the whole point is that it is seen before the paper is
                read as approved. */}
            {selectedPaper.review_staleness?.stale && (
              <div className="rp-stale-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <strong>
                    This paper was re-analysed after it was signed off.
                  </strong>
                  {selectedPaper.review_staleness.stale_signoffs.map((s) => {
                    // A re-run can land on the same number. Saying "approved at
                    // 76.3; it now scores 76.3" reads like a bug, so say what
                    // actually happened instead: the analysis is a different one,
                    // the score just agreed.
                    const now = selectedPaper.final_weighted_score;
                    const same =
                      s.reviewed_score != null &&
                      now != null &&
                      Math.abs(s.reviewed_score - now) < 0.05;
                    return (
                      <p key={s.paper_review_id}>
                        {same ? (
                          <>
                            {s.reviewer_name} approved it before that re-run; the
                            score came out the same,{" "}
                            <b>
                              {now.toFixed(1)}
                              {selectedPaper.final_rating
                                ? ` ${selectedPaper.final_rating}`
                                : ""}
                            </b>
                            , so only the per-question detail may differ.
                          </>
                        ) : (
                          <>
                            {s.reviewer_name} approved it at{" "}
                            <b>
                              {s.reviewed_score != null
                                ? s.reviewed_score.toFixed(1)
                                : "—"}
                              {s.reviewed_rating ? ` ${s.reviewed_rating}` : ""}
                            </b>
                            ; it now scores{" "}
                            <b>
                              {now != null ? now.toFixed(1) : "—"}
                              {selectedPaper.final_rating
                                ? ` ${selectedPaper.final_rating}`
                                : ""}
                            </b>
                            .
                          </>
                        )}
                        {s.basis === "backfilled"
                          ? " (Which analysis was reviewed is inferred for this" +
                            " review, not recorded at the time.)"
                          : ""}
                      </p>
                    );
                  })}
                  <p className="rp-stale-note">
                    The approval still stands as recorded - nothing has been
                    changed. Signing off again replaces it with a decision made
                    against the current score.
                  </p>
                </div>
              </div>
            )}

            {/* The whole original document, not per-question crops. */}
            <div className="review-paper-viewer">
              {paperPdfUrl ? (
                <object data={paperPdfUrl} type="application/pdf">
                  <p>
                    This browser cannot display the PDF inline.{" "}
                    <a href={paperPdfUrl} target="_blank" rel="noreferrer">
                      Open the question paper in a new tab
                    </a>
                    .
                  </p>
                </object>
              ) : (
                <p className="qr-empty">
                  <FontAwesomeIcon icon={faFilePdf} /> The original document could
                  not be loaded for this paper.
                </p>
              )}
            </div>
          </div>

          {/* Two panels, one gesture. The paper's own evaluation and the
              question-by-question review answer different questions - "is this
              paper any good" and "is this question any good" - and neither is a
              step toward the other, so stacking them made the page metres long
              and buried whichever one you were not reading. */}
          <div className="rp-tabs" role="tablist" aria-label="Paper review sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "overview"}
              className={`rp-tab ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              <FontAwesomeIcon icon={faChartSimple} />
              Question paper overview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "questions"}
              className={`rp-tab ${activeTab === "questions" ? "active" : ""}`}
              onClick={() => setActiveTab("questions")}
            >
              <FontAwesomeIcon icon={faListCheck} />
              Per question review
              {questions.length > 0 && <span className="rp-tab-count">{questions.length}</span>}
              {flaggedCount > 0 && (
                <span className="rp-tab-flag" title={`${flaggedCount} flagged`}>
                  <FontAwesomeIcon icon={faFlag} />
                </span>
              )}
            </button>
          </div>

          {activeTab === "overview" && analysis && (
            <QpEvaluation analysis={analysis} paper={selectedPaper} />
          )}
          {activeTab === "overview" && !analysis && (
            <div className="review-card">
              <p className="qr-empty">
                No completed analysis is stored for this paper, so there is no
                overview to show. The document itself is above.
              </p>
            </div>
          )}

          <div
            className="review-card"
            hidden={activeTab !== "questions"}
          >
            <div className="review-paper-head">
              <h2>Question reviews</h2>
              <span className="review-count-note">
                {questions.length} question{questions.length === 1 ? "" : "s"} analysed
                {flaggedCount > 0 ? ` · ${flaggedCount} flagged` : ""}
                {questions.length > 1 && (
                  <button
                    type="button"
                    className="qr-fold-all"
                    onClick={() => {
                      const anyOpen = questions.some(
                        (question) => openQuestions[question.question_id]);
                      const next = {};
                      if (!anyOpen) {
                        questions.forEach((question) => {
                          next[question.question_id] = true;
                        });
                      }
                      setOpenQuestions(next);
                    }}
                  >
                    {questions.some((question) => openQuestions[question.question_id])
                      ? "Collapse all"
                      : "Expand all"}
                  </button>
                )}
              </span>
            </div>

            {questions.length === 0 ? (
              <p className="qr-empty">
                No question-level analysis is stored for this paper yet.
              </p>
            ) : (
              questions.map((question) => {
                const draft = questionReviews[question.question_id] || {};
                const isSaving = savingQuestionId === question.question_id;
                const open = Boolean(openQuestions[question.question_id]);
                const summary = criteriaSummary(question);
                // The paper's own numbering, not the ordinal. A paper that
                // divides into parts restarts at 1 inside each, so "Question 11"
                // is a number that appears nowhere on the document a reviewer is
                // looking at - "Part-B · Q1" is.
                const bloom = question.bloom_name
                  || (question.blooms_level != null
                    ? BLOOM_NAMES[question.blooms_level]
                    : null);
                // Marks the walk attributed to the question against marks
                // printed beside it. Where they differ, the printed figure is
                // the one to trust, and the disagreement is worth showing
                // rather than resolving silently.
                const marksDisagree = question.declared_marks != null
                  && question.marks != null
                  && Math.abs(question.declared_marks - question.marks) > 0.01;
                return (
                  <div
                    className={`qr-block${open ? "" : " is-collapsed"}`}
                    key={question.question_id}
                  >
                    {/* The whole header row is the control, not just the caret.
                        A 12px chevron is a poor target for the commonest action
                        on the page, and everything in the row is a label for the
                        same thing. */}
                    <div
                      className="qr-block-head"
                      role="button"
                      tabIndex={0}
                      aria-expanded={open}
                      onClick={() => setOpenQuestions((prev) => ({
                        ...prev,
                        [question.question_id]: !open,
                      }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setOpenQuestions((prev) => ({
                            ...prev,
                            [question.question_id]: !open,
                          }));
                        }
                      }}
                    >
                      <span
                        className="qr-fold"
                        aria-hidden="true"
                        title={open ? "Fold this question away" : "Show this question's review"}
                      >
                        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                      </span>
                      <strong>{questionLabel(question)}</strong>
                      {/* One figure, not two. The percentage that used to sit
                          here was the same measurement as the quality badge in
                          different units - 19/24 is 79% is 7.9/10, and it agreed
                          with the badge on all 307 engine-scored questions - so
                          the row carried one number three times. It also
                          disagreed with the badge on COLOUR above 8.0/10, where
                          the percentage still read "fair" and the badge turned
                          green.
                          The denominator it also showed is not lost: a question
                          scored out of 24 rather than 28 carries the "n not
                          assessed" badge further along this row, which says the
                          same thing in words. */}
                      <QualityBadge score={question.quality_score} />
                      <span className="qr-block-meta">
                        {question.marks != null ? `${question.marks} marks` : "marks not printed"}
                        {marksDisagree ? ` (printed ${question.declared_marks})` : ""}
                        {bloom ? ` · ${bloom}` : ""}
                        {question.question_type ? ` · ${question.question_type}` : ""}
                        {Array.isArray(question.pages) && question.pages.length > 0
                          ? ` · p${question.pages.join(", ")}`
                          : ""}
                        {question.estimated_time_minutes != null
                          ? ` · ~${question.estimated_time_minutes} min`
                          : ""}
                      </span>
                      {question.topic && (
                        <span className="qr-block-topic" title="Topic assessed">
                          {question.topic}
                        </span>
                      )}
                      {/* Scored on partial content: the read reached the end of
                          the paper with this question still open. It is a full
                          seven-criterion evaluation like any other, which is
                          exactly why it needs saying - nothing else about it
                          looks any different. */}
                      {question.status === "incomplete" && (
                        <span
                          className="qr-block-warn"
                          title="This question was never marked complete while the paper was being read, so it was scored on whatever text had been captured. The page-by-page trail at the end of the evaluation above names the pages involved."
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} /> incomplete
                        </span>
                      )}
                      {summary.unscored > 0 && (
                        <span
                          className="qr-block-warn"
                          title="A criterion the model was asked to score and returned nothing usable for. It still counts toward the maximum, so this question's percentage is understated - not a judgement about the question."
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} />{" "}
                          {summary.unscored} unscored
                        </span>
                      )}
                      {summary.skipped > 0 && (
                        <span
                          className="qr-block-note"
                          title="A criterion that was never asked, because the course outcomes it depends on are unknown. It counts toward neither the score nor the maximum, so this question is scored out of a smaller total rather than marked down."
                        >
                          {summary.skipped} not assessed
                        </span>
                      )}
                      {draft.agreement === false && (
                        <span className="qr-flagged">
                          <FontAwesomeIcon icon={faFlag} /> flagged
                        </span>
                      )}
                      {draft.agreement === true && (
                        <span className="qr-agreed">
                          <FontAwesomeIcon icon={faCheckCircle} /> agreed
                        </span>
                      )}
                    </div>
                    {open && (
                    <>

                    {/* The question text is deliberately not reprinted here.
                        The whole original paper is rendered above, so this was
                        the same content twice, and it pushed the rubric - the
                        part a reviewer is here to read - below a copy of what
                        they are already looking at. */}
                    {/* A figure question's substance is the figure, and the
                        walk describes it rather than cropping it - it reports
                        which pages a question occupies and no y-position, so
                        there is nothing to cut an image from. Shown because a
                        question whose text is empty is not an empty question,
                        and the rubric below was applied to this. */}
                    {question.figure_description && (
                      <p className="qr-figure">
                        <strong>Figure or table:</strong> {question.figure_description}
                      </p>
                    )}

                    {/* The marks, then the reasoning. Seven cards of prose was
                        the first thing an opened question showed, which meant
                        the numbers a reviewer was looking for arrived last and
                        one question filled the screen. */}
                    <QuestionRubricSummary assessment={question.rubric_assessment} />

                    {/* Highlighted, because it is the one control in an open
                        question that a reviewer is meant to use: the marks
                        above are the summary and this is the evidence behind
                        them. Styled as a control rather than as the faint
                        one-line disclosures used for footnotes elsewhere. */}
                    <Disclosure
                      className="qr-rubric-detail is-prominent"
                      summary={`Rubric reasoning, criterion by criterion (${
                        Object.keys(question.rubric_assessment || {}).length
                      })`}
                    >
                      <QuestionRubric assessment={question.rubric_assessment} />
                    </Disclosure>

                    {/* Strengths and weaknesses are stored as arrays. They used
                        to be flattened with join("; ") into one paragraph each,
                        which turned a list of distinct points into prose that
                        had to be re-parsed by eye. */}
                    {((question.strengths || []).length > 0 ||
                      (question.weaknesses || []).length > 0) && (
                      <div className="qr-notes">
                        {(question.strengths || []).length > 0 && (
                          <div className="qr-note-card is-strength">
                            <h4>
                              <FontAwesomeIcon icon={faCheckCircle} /> Strengths
                            </h4>
                            <ul>
                              {question.strengths.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(question.weaknesses || []).length > 0 && (
                          <div className="qr-note-card is-weakness">
                            <h4>
                              <FontAwesomeIcon icon={faExclamationTriangle} /> Suggested
                              improvements
                            </h4>
                            <ul>
                              {question.weaknesses.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {question.blooms_justification && (
                      <p className="qr-note qr-note-bloom">
                        <strong>
                          Bloom
                          {question.blooms_level != null
                            ? ` ${question.blooms_level} - ${
                                BLOOM_NAMES[question.blooms_level] || ""
                              }`
                            : "'s"}
                          :
                        </strong>{" "}
                        {question.blooms_justification}
                      </p>
                    )}

                    <textarea
                      className="qr-feedback"
                      rows={2}
                      placeholder="Reviewer comment on this question (optional)"
                      value={draft.feedback || ""}
                      onChange={(e) => {
                        touchedQuestionFeedbackRef.current.add(question.question_id);
                        setQuestionDraft(question.question_id, {
                          feedback: e.target.value,
                        });
                      }}
                    />
                    {/* The decision, stated where it was made. Both buttons
                        used to look identical after a click, and the only
                        confirmation was a notice at the top of the page and a
                        badge above the buttons - so checking what had been
                        recorded meant scrolling away from the question. The
                        chosen button now holds its state, and the verdict is
                        spelled out beside it. */}
                    <div className="qr-block-actions">
                      <button
                        type="button"
                        className={`btn btn-secondary${
                          draft.agreement === true ? " is-chosen-agree" : ""}`}
                        disabled={isSaving}
                        aria-pressed={draft.agreement === true}
                        onClick={() => saveQuestionReview(question, true)}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} /> Agree with score
                      </button>
                      <button
                        type="button"
                        className={`btn btn-flag${
                          draft.agreement === false ? " is-chosen-flag" : ""}`}
                        disabled={isSaving}
                        aria-pressed={draft.agreement === false}
                        onClick={() => saveQuestionReview(question, false)}
                      >
                        <FontAwesomeIcon icon={faFlag} /> Dispute / flag for re-check
                      </button>
                      {isSaving && (
                        <span className="qr-decision is-saving">
                          <FontAwesomeIcon icon={faSpinner} spin /> saving…
                        </span>
                      )}
                      {!isSaving && draft.agreement === true && (
                        <span className="qr-decision is-agreed" role="status">
                          <FontAwesomeIcon icon={faCheckCircle} /> Agreed
                          {justSavedQuestionId === question.question_id
                            ? " — saved" : " — recorded"}
                        </span>
                      )}
                      {!isSaving && draft.agreement === false && (
                        <span className="qr-decision is-flagged" role="status">
                          <FontAwesomeIcon icon={faFlag} /> Flagged for re-check
                          {justSavedQuestionId === question.question_id
                            ? " — saved" : " — recorded"}
                        </span>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="review-card">
            <div className="review-paper-head">
              <h2>
                <FontAwesomeIcon icon={faCommentDots} /> Reviewer sign-off
              </h2>
              {paperReview?.review_status === "completed" && (
                <span className="qr-agreed">
                  <FontAwesomeIcon icon={faCheckCircle} /> signed off
                </span>
              )}
            </div>
            <textarea
              className="qr-feedback"
              rows={4}
              placeholder="Overall comments on this paper's review (optional)"
              value={paperFeedback}
              onChange={(e) => {
                paperFeedbackTouchedRef.current = true;
                setPaperFeedback(e.target.value);
              }}
            />
            <div className="qr-block-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={isSigningOff}
                onClick={() => savePaperReview(true)}
              >
                <FontAwesomeIcon icon={isSigningOff ? faSpinner : faCheckCircle} spin={isSigningOff} />{" "}
                Approve and sign off
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={isSigningOff}
                onClick={() => savePaperReview(false)}
              >
                Save comments only
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Review;
